import {useWindowDimensions} from 'react-native';

/**
 * One codebase, three form factors.
 *
 * The cabinet ships to a phone (Android/iOS) AND to a desktop browser at
 * cabinet.sam-vision.uz. The browser used to get the phone: a 560pt column in the
 * middle of a 1900px window with grey either side, because a layout only ever built for
 * one hand cannot use a screen. The fix is to change the SHAPE at a width where a
 * different shape is better — navigation moves to the side, lists become grids — and
 * then to let the page have the whole window (see `Screen`, which no longer caps it).
 *
 * Filling the window is what makes the COLUMN LADDER below load-bearing. A page pinned
 * to 1180pt could stop counting at three columns because there was never more room than
 * three columns of cards; an uncapped one cannot, or a 2560 monitor gets three cards
 * 760pt wide apiece, which is the same "this is a phone build, zoomed" look from the
 * other direction.
 *
 * The thresholds are the usual ones, chosen against real hardware rather than taste:
 *
 *   < 700   phone, and a browser window someone has dragged narrow. Bottom tabs.
 *   >= 700  tablet / half-screen window: two columns fit, navigation stays at the
 *           bottom because the thumb is still how it is held.
 *   >= 1024 desktop: the side rail replaces the bottom bar.
 *   >= 1560 wide: a third column of cards fits without the cards going gawky.
 *   >= 2100 ultrawide: a fourth. Past this a card stops growing and the grid does.
 *
 * Everything reads from `useWindowDimensions`, so a resized browser window re-lays out
 * live — there is no "reload to get the desktop version".
 */
export const BREAKPOINTS = {
  tablet: 700,
  desktop: 1024,
  wide: 1560,
  ultrawide: 2100,
} as const;

export type Breakpoint = {
  width: number;
  height: number;
  /** Two columns of cards fit. */
  isTablet: boolean;
  /** The side rail replaces the bottom bar. */
  isDesktop: boolean;
  /** Three columns of cards fit. */
  isWide: boolean;
  /** How many card columns a list should use at this width. */
  columns: number;
};

export function useBreakpoint(): Breakpoint {
  const {width, height} = useWindowDimensions();
  const isTablet = width >= BREAKPOINTS.tablet;
  const isDesktop = width >= BREAKPOINTS.desktop;
  const isWide = width >= BREAKPOINTS.wide;
  return {
    width,
    height,
    isTablet,
    isDesktop,
    isWide,
    columns:
      width >= BREAKPOINTS.ultrawide ? 4 : isWide ? 3 : isTablet ? 2 : 1,
  };
}
